import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Timer, Send, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

const KIND_LABEL = { verbal: "VERBALE", "abstract-visual": "ASTRATTO", numerical: "NUMERICO" };

function QuestionBody({ q, answers, setAnswers }) {
  return (
    <>
      <p className="text-[15px] leading-relaxed whitespace-pre-line mb-6 text-slate-800">{q.prompt}</p>

      {q.table && (
        <div className="border border-slate-300 mb-8 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 label-mono">
              <tr>{q.table.headers.map((h, i) => (
                <th key={i} className="text-left p-3 border-r last:border-r-0 border-slate-300">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {q.table.rows.map((row, ri) => (
                <tr key={ri} className="border-t border-slate-300">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`p-3 border-r last:border-r-0 border-slate-200 ${ci > 0 ? "font-mono-num" : "font-medium"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {q.matrix && (
        <div className="mb-8 flex justify-center">
          <div className="grid grid-cols-3 border border-slate-400" style={{ width: 360 }}>
            {q.matrix.map((cell, i) => (
              <div key={i} className={`aspect-square border border-slate-300 ${cell ? "bg-white" : "bg-slate-50 flex items-center justify-center"}`}>
                {cell
                  ? <div className="w-full h-full p-2" dangerouslySetInnerHTML={{ __html: cell }} />
                  : <span className="text-3xl font-black text-[#002FA7]">?</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={q.matrix ? "grid grid-cols-2 md:grid-cols-4 gap-3" : "space-y-3"}>
        {Object.entries(q.options).map(([k, v]) => {
          const selected = answers[q.id] === k;
          const isSvg = typeof v === "string" && v.trim().startsWith("<svg");
          if (isSvg) {
            return (
              <label key={k} data-testid={`free-opt-${q.id}-${k}`}
                className={`border cursor-pointer transition-all p-3 flex flex-col items-center ${selected ? "border-[#002FA7] bg-[#002FA7]/5 shadow-hard" : "border-slate-200 hover:border-slate-400"}`}>
                <input type="radio" name={q.id} className="sr-only" checked={selected}
                  onChange={() => setAnswers({ ...answers, [q.id]: k })} />
                <span className="label-mono text-[#002FA7] mb-2">{k}</span>
                <div className="w-20 h-20" dangerouslySetInnerHTML={{ __html: v }} />
              </label>
            );
          }
          return (
            <label key={k} data-testid={`free-opt-${q.id}-${k}`}
              className={`flex gap-4 p-4 border cursor-pointer transition-colors ${selected ? "border-[#002FA7] bg-[#002FA7]/5" : "border-slate-200 hover:border-slate-400"}`}>
              <input type="radio" name={q.id} className="mt-1 accent-[#002FA7]" checked={selected}
                onChange={() => setAnswers({ ...answers, [q.id]: k })} />
              <div><span className="label-mono mr-3 text-[#002FA7]">{k}</span><span>{v}</span></div>
            </label>
          );
        })}
      </div>
    </>
  );
}

export default function FreeTest() {
  const { category } = useParams();
  const nav = useNavigate();
  const [test, setTest] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(0);
  const [result, setResult] = useState(null);
  const started = useRef(Date.now());

  useEffect(() => {
    api.get(`/free/${category}`)
      .then((r) => { setTest(r.data); setRemaining(r.data.duration_minutes * 60); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); nav("/free"); });
  }, [category, nav]);

  useEffect(() => {
    if (!test || remaining <= 0 || result) return;
    const id = setInterval(() => setRemaining((r) => r - 1), 1000);
    return () => clearInterval(id);
  }, [test, remaining, result]);

  useEffect(() => { if (test && remaining === 0 && !result) submit(); /* eslint-disable-next-line */ }, [remaining]);

  const submit = async () => {
    if (!test) return;
    const payload = {
      category,
      answers: test.questions.map((q) => ({ question_id: q.id, answer: answers[q.id] || null })),
    };
    try {
      const { data } = await api.post("/free/submit", payload);
      setResult(data);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!test) return <Layout><div className="max-w-5xl mx-auto p-8 text-slate-500">Caricamento…</div></Layout>;

  // ---------------- RESULT VIEW ----------------
  if (result) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
          <div className="border border-slate-200 bg-white p-10 mb-10 shadow-hard-black" data-testid="free-result-score">
            <div className="label-mono text-[#002FA7] mb-2">— Punteggio</div>
            <h1 className="font-display font-black text-6xl tracking-tighter">{result.score_percent}%</h1>
            <p className="text-slate-600 mt-4">{result.correct} corrette su {result.total} · {result.title}</p>
          </div>

          <div className="border border-[#002FA7] bg-[#0A0F1A] text-white p-8 mb-10 premium-grain flex flex-col md:flex-row md:items-center justify-between gap-5">
            <p className="text-slate-200 max-w-xl leading-relaxed">
              Hai completato la prova gratuita. Sblocca <span className="text-[#FFD700] font-semibold">450 domande con spiegazione</span> e
              le guide con il metodo passo-passo.
            </p>
            <div className="flex gap-3">
              <Link to="/pricing"><Button data-testid="free-result-pricing" className="rounded-none bg-[#FFD700] hover:bg-white text-[#0A0F1A] label-mono py-6 px-6">Vedi i prezzi</Button></Link>
              <Link to="/register"><Button data-testid="free-result-register" variant="outline" className="rounded-none border-white text-white hover:bg-white hover:text-[#0A0F1A] label-mono py-6 px-6">Registrati</Button></Link>
            </div>
          </div>

          <h2 className="font-display font-bold text-2xl mb-6">Revisione domande</h2>
          <div className="space-y-4">
            {result.details.map((d, i) => (
              <div key={d.question_id} data-testid={`free-review-${i}`}
                className={`border p-6 bg-white ${d.is_correct ? "border-green-300" : "border-red-300"}`}>
                <div className="flex items-center gap-2 mb-3">
                  {d.is_correct ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                  <span className="label-mono">{i + 1}. {d.is_correct ? "Corretto" : "Sbagliato"}</span>
                </div>
                <p className="whitespace-pre-line text-sm mb-3 text-slate-700">{d.prompt}</p>
                {d.table && (
                  <div className="border border-slate-300 mb-3 overflow-x-auto text-xs">
                    <table className="w-full">
                      <thead className="bg-slate-100 label-mono">
                        <tr>{d.table.headers.map((h, j) => (<th key={j} className="text-left p-2 border-r last:border-r-0 border-slate-300">{h}</th>))}</tr>
                      </thead>
                      <tbody>
                        {d.table.rows.map((row, ri) => (
                          <tr key={ri} className="border-t border-slate-300">
                            {row.map((cell, ci) => (<td key={ci} className="p-2 border-r last:border-r-0 border-slate-200 font-mono-num">{cell}</td>))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {d.matrix && (
                  <div className="mb-3 flex gap-4 items-center">
                    <div className="grid grid-cols-3 border border-slate-400" style={{ width: 210 }}>
                      {d.matrix.map((cell, j) => (
                        <div key={j} className={`aspect-square border border-slate-300 ${cell ? "bg-white" : "bg-slate-50 flex items-center justify-center"}`}>
                          {cell ? <div className="w-full h-full p-1" dangerouslySetInnerHTML={{ __html: cell }} /> : <span className="text-xl font-black text-[#002FA7]">?</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-sm space-y-1">
                  <div>La tua risposta: <span className="font-mono-num font-bold">{d.your_answer || "—"}</span>
                    {d.your_answer && typeof d.options[d.your_answer] === "string" && !d.options[d.your_answer].startsWith("<svg") && (
                      <span className="ml-2 text-slate-600">— {d.options[d.your_answer]}</span>
                    )}
                  </div>
                  <div>Risposta corretta: <span className="font-mono-num font-bold text-[#002FA7]">{d.correct_answer}</span>
                    {typeof d.options[d.correct_answer] === "string" && !d.options[d.correct_answer].startsWith("<svg") && (
                      <span className="ml-2">— {d.options[d.correct_answer]}</span>
                    )}
                  </div>
                  {typeof d.options[d.correct_answer] === "string" && d.options[d.correct_answer].startsWith("<svg") && (
                    <div className="flex gap-3 items-center mt-2 label-mono text-xs text-slate-500">
                      <span>Corretta:</span>
                      <div className="w-14 h-14 border border-[#002FA7]" dangerouslySetInnerHTML={{ __html: d.options[d.correct_answer] }} />
                    </div>
                  )}
                </div>
                {d.explanation && (
                  <div className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-600">
                    <span className="label-mono text-slate-400">Spiegazione: </span>{d.explanation}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 flex gap-4">
            <Link to="/free"><Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-6">Prova un altro tipo</Button></Link>
            <Link to="/pricing"><Button variant="outline" className="rounded-none border-slate-900 label-mono py-6">Sblocca la banca completa <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------------- TEST VIEW ----------------
  const q = test.questions[idx];
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const answered = Object.keys(answers).length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="border border-slate-200 bg-white flex items-center justify-between p-4 mb-6">
          <div>
            <div className="label-mono text-slate-500">{test.title}</div>
            <div className="font-display font-bold text-lg">Domanda {idx + 1} di {test.questions.length}</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="label-mono text-slate-500">Risposte: <span className="font-mono-num text-slate-900">{answered}/{test.questions.length}</span></div>
            <div className={`flex items-center gap-2 px-4 py-2 border ${remaining < 60 ? "border-red-500 text-red-600" : "border-slate-300"}`}>
              <Timer className="w-4 h-4" /><span className="font-mono-num text-lg font-bold">{mm}:{ss}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 border border-slate-200 bg-white p-8">
            <div className="label-mono text-[#002FA7] mb-4">{KIND_LABEL[q.kind] || "MISTO"}</div>
            <QuestionBody q={q} answers={answers} setAnswers={setAnswers} />
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button variant="outline" className="rounded-none label-mono" data-testid="free-prev-q"
                disabled={idx === 0} onClick={() => setIdx(idx - 1)}>Precedente</Button>
              {idx < test.questions.length - 1 ? (
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono" data-testid="free-next-q"
                  onClick={() => setIdx(idx + 1)}>Successiva</Button>
              ) : (
                <Button className="rounded-none bg-[#FFD700] hover:bg-yellow-300 text-[#0A0F1A] label-mono" data-testid="free-submit-btn" onClick={submit}>
                  <Send className="w-4 h-4 mr-2" />Termina e correggi
                </Button>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 border border-slate-200 bg-white p-4 h-fit sticky top-20">
            <div className="label-mono text-slate-500 mb-3">Domande</div>
            <div className="grid grid-cols-5 gap-2">
              {test.questions.map((qq, i) => {
                const ans = !!answers[qq.id];
                const cur = i === idx;
                return (
                  <button key={qq.id} onClick={() => setIdx(i)} data-testid={`free-goto-q-${i}`}
                    className={`aspect-square text-sm font-mono-num border ${cur ? "border-[#002FA7] bg-[#002FA7] text-white" : ans ? "border-[#002FA7] text-[#002FA7]" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <Button className="w-full mt-4 rounded-none bg-[#0A0F1A] hover:bg-slate-800 label-mono" data-testid="free-submit-side" onClick={submit}>
              Termina e correggi
            </Button>
            <Link to="/free" className="block text-center text-xs text-slate-500 mt-3 hover:underline">← esci</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
